import React from 'react';

import BadgeIcon from '@mui/icons-material/Badge';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Chip, { ChipProps } from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

type AttendanceValue = 'present' | 'absent' | 'no_permit' | null;

interface AttendanceChipProps extends Partial<ChipProps> {
  value: AttendanceValue;
}

interface OwnerState {
  ownerState: AttendanceChipProps;
}

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'ownerState', // prevent custom prop from reaching the DOM
})<OwnerState>(({ theme, ownerState: { value } }) => ({
  // common styles
  fontWeight: 'bold',

  '& .MuiChip-icon': {
    color: 'inherit',
  },

  // conditional styles based on ownerState.value
  ...(value === 'present' && {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
  }),
  ...(value === 'absent' && {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  }),
  ...(value === 'no_permit' && {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
  }),
}));

function AttendanceChipComponent({ value, ...props }: AttendanceChipProps) {
  if (value === null) {
    return null;
  }

  const chipProps: Record<NonNullable<AttendanceValue>, Partial<ChipProps>> = {
    present: {
      icon: <CheckIcon fontSize="medium" />,
      label: 'Present',
    },
    absent: {
      icon: <CloseIcon fontSize="medium" />,
      label: 'No-Show',
    },
    no_permit: {
      icon: <BadgeIcon fontSize="medium" />,
      label: 'No Permit',
    },
  };

  return <StyledChip ownerState={{ value: value }} size="small" variant="outlined" {...chipProps[value]} {...props} />;
}

// memoize component
export const AttendanceChip = React.memo(AttendanceChipComponent);

export default AttendanceChip;
